"use client";

import { Share2 } from "lucide-react";
import { forwardRef } from "react";
import { SendInChatButton } from "@/components/share/send-in-chat-button";
import { DETAIL_DESKTOP } from "@/components/shell/detail-page-desktop";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackButton } from "@/components/ui/back-button";
import { mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import type { Hiive } from "./hiive-page.types";

interface HiiveStickyHeaderProps {
  hiive: Hiive;
  showStickyTitle: boolean;
}

export const HiiveStickyHeader = forwardRef<
  HTMLDivElement,
  HiiveStickyHeaderProps
>(({ hiive, showStickyTitle }, headerRef) => {
  return (
    <div
      ref={headerRef}
      className={cn(
        "pointer-events-none fixed top-0 right-0 left-0 z-1001 border-b border-transparent p-4 pt-[calc(env(safe-area-inset-top)+10px)] pr-[calc(1rem+env(safe-area-inset-right,0px))] pl-[calc(1rem+env(safe-area-inset-left,0px))] transition-all duration-300 wide:pr-[env(safe-area-inset-right,0px)] wide:pl-[env(safe-area-inset-left,0px)]",
        showStickyTitle &&
          "pointer-events-auto border-glass-border-10 bg-background/80 backdrop-blur-md",
        DETAIL_DESKTOP.barSurface,
        DETAIL_DESKTOP.barColumn,
        DETAIL_DESKTOP.barCorner,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-3",
          DETAIL_DESKTOP.barInner,
          DETAIL_DESKTOP.barColumnRow,
        )}
      >
        <BackButton
          fallbackHref="/hiives"
          label="Back"
          className={cn(
            "pointer-events-auto ml-0 h-10 w-10 rounded-full bg-black/40 text-white shadow-lg backdrop-blur-sm hover:bg-black/60",
            DETAIL_DESKTOP.barButton,
            DETAIL_DESKTOP.barColumnButton,
          )}
        />
        <div
          className={cn(
            "pointer-events-auto flex items-center gap-3 rounded-full bg-black/40 px-4 py-2 opacity-0 backdrop-blur-sm transition-all duration-300",
            DETAIL_DESKTOP.barTitle,
            showStickyTitle && "bg-transparent px-2 opacity-100",
          )}
        >
          {hiive.logoImage && (
            <Image
              src={mediaUrlFromAsset(hiive.logoImage) ?? ""}
              alt={hiive.name}
              width={24}
              height={24}
              className="h-6 w-6 rounded-md object-cover"
            />
          )}
          <span className="font-semibold text-text-primary shadow-black/50 drop-shadow-md">
            {hiive.name}
          </span>
        </div>
        <SendInChatButton
          entityType="hiive"
          entityId={String(hiive.id)}
          aria-label="Share hiive"
          className={cn(
            "pointer-events-auto ml-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/40 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black/60",
            DETAIL_DESKTOP.barButton,
            DETAIL_DESKTOP.barColumnButton,
          )}
          nativeShare={{ title: hiive.name }}
        >
          <Share2 className="size-7" />
        </SendInChatButton>
      </div>
    </div>
  );
});

HiiveStickyHeader.displayName = "HiiveStickyHeader";
